import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Filter, Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, serviceByKey } from "../lib/api";
import { format } from "date-fns";

const STATUS_STYLES = {
  pending: "bg-primary/15 text-primary border border-primary/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40",
  declined: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
};

const StatCard = ({ label, value, accent }) => (
  <div className="border hairline bg-card p-6 relative">
    <span className="label-eyebrow">{label}</span>
    <p className={`font-display text-5xl mt-3 ${accent ? "gold-gradient-text" : ""}`}>{value}</p>
  </div>
);

const Admin = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, declined: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: bks }, { data: st }] = await Promise.all([
        api.get("/bookings"),
        api.get("/stats"),
      ]);
      setBookings(bks);
      setStats(st);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === "all") return bookings;
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      fetchAll();
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-32" data-testid="admin-section">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <span className="label-eyebrow">— Studio control</span>
            <h1 className="font-display mt-4 text-5xl lg:text-6xl leading-[0.98]">
              Appointment <span className="gold-gradient-text">queue.</span>
            </h1>
          </div>
          <Button
            variant="outline"
            onClick={fetchAll}
            className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          <StatCard label="Total" value={stats.total} accent />
          <StatCard label="Pending" value={stats.pending} />
          <StatCard label="Confirmed" value={stats.confirmed} />
          <StatCard label="Declined" value={stats.declined} />
        </div>

        <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
          <p className="label-eyebrow">— Bookings</p>
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                className="w-44 rounded-none bg-secondary border-border h-10"
                data-testid="admin-filter-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="admin-filter-all">All</SelectItem>
                <SelectItem value="pending" data-testid="admin-filter-pending">Pending</SelectItem>
                <SelectItem value="confirmed" data-testid="admin-filter-confirmed">Confirmed</SelectItem>
                <SelectItem value="declined" data-testid="admin-filter-declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 border hairline bg-card overflow-x-auto" data-testid="admin-table-wrap">
          {(() => {
            if (loading) {
              return (
                <div className="flex items-center justify-center py-24 text-muted-foreground">
                  <Loader2 className="mr-3 h-5 w-5 animate-spin" /> Loading queue…
                </div>
              );
            }
            if (filtered.length === 0) {
              return (
                <div className="py-20 text-center text-muted-foreground" data-testid="admin-empty">
                  No bookings to show.
                </div>
              );
            }
            return (
            <Table>
              <TableHeader>
                <TableRow className="border-b hairline hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Ref</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Service</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">When</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Status</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-[0.22em] text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} className="border-b hairline hover:bg-secondary/30" data-testid={`admin-row-${b.id}`}>
                    <TableCell className="font-mono text-xs text-primary">{b.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>
                      <div className="font-display text-lg">{b.full_name}</div>
                      <div className="text-xs text-muted-foreground">{b.address}</div>
                    </TableCell>
                    <TableCell className="text-sm">{serviceByKey(b.service_type).short}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {format(new Date(b.preferred_date), "do MMM yyyy")}
                      {b.preferred_time && <div className="text-xs text-muted-foreground">{b.preferred_time}</div>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{b.email}</div>
                      <div className="text-xs text-muted-foreground">{b.phone}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-none px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[b.status]}`}>
                        {b.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          disabled={busyId === b.id || b.status === "confirmed"}
                          onClick={() => updateStatus(b.id, "confirmed")}
                          className="rounded-none bg-primary text-primary-foreground hover:bg-accent h-9"
                          data-testid={`admin-confirm-${b.id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === b.id || b.status === "declined"}
                          onClick={() => updateStatus(b.id, "declined")}
                          className="rounded-none border-rose-500/60 text-rose-300 hover:bg-rose-500/10 h-9"
                          data-testid={`admin-decline-${b.id}`}
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Decline
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Admin;
